import { create } from 'zustand';
import { 
  fetchAccounts, 
  fetchActiveAccounts, 
  deleteAccountById, 
  fetchAccountPositions,
  recalculateAccountPosition, // Add this import
  fetchPMUsers // Import fetchPMUsers
} from '../http/api';

/**
 * Store for managing account positions data
 * Responsible for account listing, selection, and position data
 */
export const useAccountPositionStore = create((set, get) => ({
  // State
  accounts: [],
  selectedAccount: null,
  accountSummary: {
    totalPar: null,
    averageYieldToWorst: null,
    average: null,
    interest: null,
    averageSPRating: null,
    taxable: null,
    totalCurrValue: null,
    avgYieldToMaturity: null,
    convexity: null,
    next12MonthInterest: null,
    next12MonthPrincipal: null,
    averageMoodys: null,
    nonTaxable: null,
    totalDv01: null, // Add totalDv01 to account summary
  },
  positions: [],
  pagination: {
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 20
  },
  isAddAccountModalOpen: false,
  isEditAccountModalOpen: false,
  editingAccount: null,
  isLoading: false,
  error: null,
  // Add notification state
  notification: null,
  pmUsers: [], // PM users state
  
  // Add pagination state for accounts
  accountsPagination: {
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasMore: true,
    isLoadingMore: false
  },
  
  // Add filter and sort state
  accountsFilter: {
    userId: 0,
    sortDirection: 'default',
    columnToSort: 'updatedAt'
  },

  // Account selection
  setSelectedAccount: (accountId) => set({ selectedAccount: accountId }),
  
  // Notification handling
  showNotification: (message, severity = 'success') => {
    set({ notification: { message, severity, id: Date.now() } });
    
    // Auto-clear notification after display
    setTimeout(() => {
      // Only clear if it's the same notification (prevent clearing newer ones)
      set(state => {
        if (state.notification && state.notification.id === Date.now()) {
          return { notification: null };
        }
        return {};
      });
    }, 5000);
  },
  
  clearNotification: () => set({ notification: null }),
  
  // Account CRUD operations
  addAccount: (accountData) => {
    const { account, positions = [] } = accountData;
    
    set(state => {
      // Ensure account identifier has ACC- prefix
      let accountIdentifier = account.accountIdentifier;
      if (accountIdentifier && !accountIdentifier.startsWith('ACC-')) {
        accountIdentifier = 'ACC-' + accountIdentifier;
      }
      
      const newAccount = { 
        ...account, 
        id: account.accountId || Date.now(),
        accountIdentifier: accountIdentifier,
        accountNumber: accountIdentifier,
        positions: positions.map(pos => ({
          cusip: pos.cusip,
          quantity: pos.quantity,
          price: pos.price,
          secMasterId: pos.secMasterId,
          bondInventoryId: pos.bondInventoryId,
          accountPositionMappingId: pos.accountPositionMappingId || null
        }))
      };
      
      // Show success notification
      get().showNotification(`Account "${account.accountIdentifier}" created successfully`);
      
      return { accounts: [...state.accounts, newAccount] };
    });
  },
  
  updateAccount: (accountId, updatedData) => {
    const { account, positions } = updatedData;
    
    set(state => {
      // Show success notification
      get().showNotification(`Account "${account.accountIdentifier}" updated successfully`);
      
      return {
        accounts: state.accounts.map(acc => {
          if (acc.id === accountId) {
            return { 
              ...acc, 
              ...account,
              positions: positions ? positions.map(pos => ({
                cusip: pos.cusip,
                quantity: pos.quantity,
                price: pos.price,
                secMasterId: pos.secMasterId,
                bondInventoryId: pos.bondInventoryId,
                accountPositionMappingId: pos.accountPositionMappingId || null
              })) : acc.positions
            };
          }
          return acc;
        })
      };
    });
  },
  
  deleteAccount: async (id, token) => {
    set({ isLoading: true, error: null });
    try {
      // Get account info before removing from state

      const accountToDelete = get().accounts.find(acc => acc.id === id);
      const accountName = accountToDelete?.accountIdentifier || 'Unknown';
      // Call API to delete account
      await deleteAccountById(token, id);
      
      // Show success notification
      get().showNotification(`Account "${accountName}" deleted successfully`);
      
      // WebSocket event will handle state update
      set({ isLoading: false });
      
      return true;
    } catch (error) {
      set({ error: error.message || 'Failed to delete account', isLoading: false });
      throw error; // Re-throw to allow handling in components
    }
  },
  
  // Modal state management
  setAddAccountModalOpen: (isOpen) => set({ isAddAccountModalOpen: isOpen }),
  
  setEditAccountModalOpen: (isOpen, account = null) => set({ 
    isEditAccountModalOpen: isOpen,
    editingAccount: account
  }),
  
  // Data fetching
  fetchAccountData: async (reset = true, userIdParam = null) => {
    const state = get();
    
    // Resolve userId: prefer param, then filter, then localStorage
    const resolvedUserId = userIdParam || state.accountsFilter?.userId || localStorage.getItem('userId') || 0;
    
    // If reset, start from page 0, otherwise continue from current page
    const page = reset ? 0 : state.accountsPagination.currentPage;
    const { sortDirection, columnToSort } = state.accountsFilter;
    
    // Don't fetch if we're already at the end and not resetting
    if (!reset && !state.accountsPagination.hasMore) {
      return;
    }
    
    // Set loading states
    if (reset) {
      set({ 
        isLoading: true, 
        error: null,
        // Ensure accounts array is empty on reset
        accounts: []
      });
    } else {
      set({ 
        accountsPagination: { 
          ...state.accountsPagination, 
          isLoadingMore: true 
        } 
      });
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetchActiveAccounts(
        token, 
        page, 
        state.accountsPagination.pageSize, 
        resolvedUserId,
        columnToSort, 
        sortDirection
      );
      
      if (!response || !response.content) {
        throw new Error('Invalid response from server');
      }
      
      // Map the API response to match our expected format
      const formattedAccounts = response.content.map(account => ({
        id: account.accountId,
        accountId: account.accountId,
        // userId: account.userId,
        userName: account.userName,
        description: account.description,
        custodianName: account.custodianName || 'N/A',
        custodianId: account.custodianId,
        accountNumber: account.accountNumber || account.accountIdentifier,
        accountIdentifier: account.accountIdentifier,
        accountType: account.accountType || 'Bond',
        isEditable: account.isEditable,
        status: account.status,
        entryBy: account.entryBy,
        positions: []
      }));
      
      const previousSelectedId = get().selectedAccount;
      
      if (reset) {
        // Reset: replace accounts array completely
        // Get the current accounts array to ensure it's really empty
        const currentAccounts = get().accounts;
        if (currentAccounts.length > 0) {
          // If there are somehow still accounts in the array, this is a fail-safe
          console.warn('Accounts array not empty before reset - forcing clear');
          // Force clearing the accounts array again to be absolutely sure
          set({ accounts: [] });
        }
        
        const hasSelectedAccount = previousSelectedId && formattedAccounts.some(acc => acc.id === previousSelectedId);
        let newSelectedId;
        
        if (formattedAccounts.length === 0) {
          // No accounts available - clear selection and clear positions/summary
          newSelectedId = null;
          set({
            positions: [],
            accountSummary: {
              totalPar: null,
              averageYieldToWorst: null,
              average: null,
              interest: null,
              averageSPRating: null,
              taxable: null,
              totalCurrValue: null,
              avgYieldToMaturity: null,
              convexity: null,
              next12MonthInterest: null,
              next12MonthPrincipal: null,
              averageMoodys: null,
              nonTaxable: null,
              totalDv01: null
            },
            pagination: {
              totalElements: 0,
              totalPages: 0,
              currentPage: 0,
              pageSize: 20
            }
          });
        } else {
          // Accounts available - select appropriately
          newSelectedId = hasSelectedAccount 
            ? previousSelectedId 
            : formattedAccounts[0].id;
        }
          
        set({ 
          accounts: formattedAccounts, // Use new data only
          selectedAccount: newSelectedId,
          accountsPagination: {
            currentPage: response.last ? page : page + 1,
            pageSize: response.size,
            totalElements: response.totalElements,
            totalPages: response.totalPages,
            hasMore: !response.last,
            isLoadingMore: false
          },
          isLoading: false
        });
      } else {
        // Append: add to existing accounts array (for infinite scroll)
        const currentAccounts = get().accounts;
        
        // Create a Set of existing account IDs to prevent duplicates
        const existingAccountIds = new Set(currentAccounts.map(acc => acc.id));
        
        // Filter out any accounts that are already in the list
        const uniqueNewAccounts = formattedAccounts.filter(acc => !existingAccountIds.has(acc.id));
        
        // Only append unique accounts
        const newAccounts = [...currentAccounts, ...uniqueNewAccounts];
        
        // Log if we found and filtered duplicates
        if (uniqueNewAccounts.length !== formattedAccounts.length) {
          console.warn(`Filtered out ${formattedAccounts.length - uniqueNewAccounts.length} duplicate accounts`);
        }
        
        set({ 
          accounts: newAccounts,
          accountsPagination: {
            currentPage: response.last ? page : page + 1,
            pageSize: response.size,
            totalElements: response.totalElements,
            totalPages: response.totalPages,
            hasMore: !response.last,
            isLoadingMore: false
          },
          isLoading: false
        });
      }
      
    } catch (error) {
      console.error("Error fetching account data:", error);
      set({ 
        error: error.message || 'Failed to fetch account data', 
        isLoading: false,
        accountsPagination: {
          ...get().accountsPagination,
          isLoadingMore: false
        }
      });
    }
  },
  
  // New method to load more accounts
  loadMoreAccounts: async () => {
    const state = get();
    if (state.accountsPagination.hasMore && !state.accountsPagination.isLoadingMore) {
      await get().fetchAccountData(false);
    }
  },
  
  // New method to update filters and sorting
  updateAccountsFilter: async (newFilter) => {
    const currentFilter = get().accountsFilter;
    const updatedFilter = { ...currentFilter, ...newFilter };
    const forceReset = newFilter.forceReset === true;
    
    // Remove the internal flag from the filter object before saving
    delete updatedFilter.forceReset;
    
    // Determine columnToSort based on sortDirection
    if (updatedFilter.sortDirection === 'default') {
      updatedFilter.columnToSort = 'updatedAt';
    } else {
      updatedFilter.columnToSort = 'accountIdentifier';
    }
    
    // First set: Clear accounts array immediately and update filters
    set({ 
      // This is crucial - set accounts to empty array before fetching
      accounts: [], 
      accountsFilter: updatedFilter,
      accountsPagination: {
        currentPage: 0,
        pageSize: 20,
        totalElements: 0,
        totalPages: 0,
        hasMore: true,
        isLoadingMore: false
      },
      isLoading: true // Set loading state to prevent further user interaction
    });
    
    try {
      // Fetch with reset ALWAYS true when filter/sort changes
      await get().fetchAccountData(true);
    } catch (error) {
      console.error('Error fetching accounts with new filter:', error);
      // Ensure loading state is cleared on error
      set({ isLoading: false });
    }
  },

  // Fetch account positions
  fetchAccountPositions: async (accountId, token, page = 0, size = 20, columnToSort = "updatedAt", sortingDirection = "desc") => {
    if (!accountId || !token) return Promise.resolve();
    
    set({ isLoading: true, error: null });
    try {
      if(columnToSort === "securityWeightPercentage") {
        columnToSort = "securityMarketValue";
      }
      const response = await fetchAccountPositions(token, accountId,null, page, size, columnToSort, sortingDirection);
      
      // --- Updated parsing for new API response structure ---
      // response.data.content: positions array
      // response.summary: summary object
      // response.accountInfo: account-level info (custodian, description, etc)
      if (!response || !response.data || !response.data.content) {
        throw new Error('Invalid response from server');
      }
      
      // Transform the API response to match our table structure
      const transformedPositions = response.data.content.map(position => ({
        id: position.accountPositionMappingId,
        accountId: position.accountMstId,
        cusip: position.cusip,
        securityDesc: position.securityDesc, // backend: securityDesc
        description: position.securityDesc, // legacy field for backward compatibility
        quantity: position.quantity,
        securityMarketValue: position.securityMarketValue,
        totalGL: (position.securityRealizedGL || 0) + (position.securityUnrealizedGL || 0),
        realizedGL: position.securityRealizedGL || 0,
        unrealizedGL: position.securityUnrealizedGL || 0,
        maturityDate: position.maturityDate, // backend: maturityDate
        maturity: position.maturityDate, // legacy field for backward compatibility
        nextCallDate: position.nextCallDate,
        spRating: position.spRating, // backend: spRating
        ratingSnp: position.spRating, // legacy field for backward compatibility
        moodyRating: position.moodyRating, // backend: moodyRating
        ratingMoody: position.moodyRating, // legacy field for backward compatibility
        isExemptFromFedTax: position.isExemptFromFedTax,
        yield: position.yield,
        yieldToWorst: position.yieldToWorst,
        yieldValuePp: position.yieldValuePp,
        duration: position.duration,
        modifiedConvexity: position.modifiedConvexity,
        callStartDate: position.callStartDate,
        callEndDate: position.callEndDate,
        dollarConvexity: position.dollarConvexity,
        dv01: position.dv01,
        macaulayDuration: position.macaulayDuration,
        accruedInterest: position.accruedInterest,
        bondType: position.bondType,
        isCallable: position.isCallable, // backend: isCallable
        callable: position.isCallable, // legacy field for backward compatibility
        callPrice: position.callPrice,
        price: position.price,
        couponPer: position.couponPer, // backend: couponPer
        coupon: position.couponPer, // legacy field for backward compatibility
        secMasterId: position.secMasterId,
        bondInventoryId: position.bondInventoryId,
        accountPositionMappingId: position.accountPositionMappingId,
        lastUpdatedBondCal: position.lastUpdatedBondCal,
        conventionMethod: position.conventionMethod,
        incomeSource: position.incomeSource,
        bondSubType: position.bondSubType,
        state: position.state,
        sector: position.sector,
        bondCalcOutputId: position.bondCalcOutputId,
        firstCouponDate: position.firstCouponDate,
        isZeroCoupon: position.isZeroCoupon,
        securityWeightPercentage: position.securityWeightPercentage
      }));

      // Helper function to distinguish between zero and undefined/null
      const getValue = (val) => (val === 0 || val === 0.0 || val) ? val : null;

      // Extract and map summary data from the API response
      const summary = response.summary || {};
      const accountInfo = response.accountInfo || {};

      const updatedSummary = {
        totalPar: getValue(summary.totalParValue),
        totalCurrValue: getValue(summary.totalCurrentValue),
        averageYieldToWorst: getValue(summary.averageYieldToWorst),
        avgYieldToMaturity: getValue(summary.averageYieldToMaturity),
        average: getValue(summary.averageDuration),
        convexity: getValue(summary.averageConvexity), 
        interest: getValue(summary.interest),
        next12MonthInterest: getValue(summary.next12MonthInterest),
        next12MonthPrincipal: getValue(summary.next12MonthPrincipal),
        averageSPRating: summary.averageSPRating || null,
        averageMoodys: summary.averageMoodyRating || null,
        nonTaxable: getValue(summary.totalNonTaxable),
        taxable: getValue(summary.totalTaxable),
        totalDv01: getValue(summary.totalDv01),
        // Add moved fields from accountInfo
        accountMstId: accountInfo.accountMstId,
        accountIdentifier: accountInfo.accountIdentifier,
        accountDescription: accountInfo.accountDescription,
        custodianName: accountInfo.custodianName,
        // userId: accountInfo.userId,
        userName: accountInfo.userName
      };
      
      set({
        positions: transformedPositions,
        accountSummary: updatedSummary,
        pagination: {
          totalElements: response.data.totalElements || 0,
          totalPages: response.data.totalPages || 1,
          currentPage: response.data.number || 0,
          pageSize: response.data.size || 20
        },
        isLoading: false
      });
      
      // Start bond recalculation check asynchronously
      setTimeout(() => {
        checkAndRecalculatePositions(transformedPositions, token);
      }, 100);
      
      return Promise.resolve();
    } catch (error) {
      console.error("Error fetching account positions:", error);
      set({ 
        error: error.message || 'Failed to fetch account positions', 
        isLoading: false,
        positions: [],
        accountSummary: {
          totalPar: null,
          averageYieldToWorst: null,
          average: null,
          interest: null,
          averageSPRating: null,
          taxable: null,
          totalCurrValue: null,
          avgYieldToMaturity: null,
          convexity: null,
          next12MonthInterest: null,
          next12MonthPrincipal: null,
          averageMoodys: null,
          nonTaxable: null,
        }
      });
      return Promise.reject(error);
    }
  },

  // Fetch PM users
  fetchPMUsers: async (token) => {
    if (!token) return;
    
    try {
      const data = await fetchPMUsers(token);
      if (!data || !Array.isArray(data)) {
        throw new Error('Invalid PM users response format');
      }
      set({ pmUsers: data });
      return data;
    } catch (error) {
      console.error("Error fetching PM users:", error);
      set({ error: error.message || 'Failed to fetch PM users' });
      return [];
    }
  },

  // Helper methods
  getAccountForEdit: (accountId) => {
    const state = get();
    const account = state.accounts.find(acc => acc.id === accountId);
    if (!account) return null;
    
    // First check if the account has positions already stored
    if (account.positions && account.positions.length > 0) {
      return account;
    }
    
    // Otherwise, find positions from the current positions array that match this account
    const accountPositions = state.positions
      .filter(pos => pos.accountId === accountId)
      .map(pos => ({
        cusip: pos.cusip || pos.securityId,
        quantity: pos.quantity,
        price: pos.price, // Default price if not available
        secMasterId: pos.secMasterId,
        bondInventoryId: pos.bondInventoryId,
        accountPositionMappingId: pos.accountPositionMappingId
      }));
    
    return {
      ...account,
      userName: account.userName || '',
      accountIdentifier: account.accountIdentifier || account.accountNumber,
      positions: accountPositions
    };
  },

  // Error handling
  setError: (errorMessage) => set({ error: errorMessage }),
  clearError: () => set({ error: null }),
}));

/**
 * Helper function to check if a position's bond calculation needs updating
 * @param {string} lastUpdatedBondCalDate - UTC timestamp of last calculation
 * @returns {boolean} - Whether recalculation is needed
 */
function needsRecalculation(lastUpdatedBondCalDate) {
  if (!lastUpdatedBondCalDate) return false;

  // Ensure the timestamp is treated as UTC by appending 'Z' if not present
  const timestamp = lastUpdatedBondCalDate.endsWith('Z')
    ? lastUpdatedBondCalDate
    : lastUpdatedBondCalDate + 'Z';

  // Parse as UTC
  const lastUpdated = new Date(timestamp);

  // Handle invalid date
  if (isNaN(lastUpdated)) return false;

  // Get current UTC date
  const now = new Date();

  // Compare UTC year, month, and day
  return (
    lastUpdated.getUTCFullYear() !== now.getUTCFullYear() ||
    lastUpdated.getUTCMonth() !== now.getUTCMonth() ||
    lastUpdated.getUTCDate() !== now.getUTCDate()
  );
}

/**
 * Checks all positions and triggers recalculations where needed
 * @param {Array} positions - List of positions to check
 * @param {string} token - Authentication token
 */
async function checkAndRecalculatePositions(positions, token) {
  if (!positions || !positions.length || !token) return;

  console.log("Checking positions for bond recalculation needs...");
  
  const positionsNeedingUpdate = positions.filter(position => 
    position.lastUpdatedBondCal!=null && position.accountPositionMappingId && needsRecalculation(position.lastUpdatedBondCal)
  );
  
  if (positionsNeedingUpdate.length === 0) {
    console.log("All positions are up to date with bond calculations.");
    return;
  }
  
  console.log(`Found ${positionsNeedingUpdate.length} positions needing bond recalculation.`);
  
  // Process each position that needs recalculation
  for (const position of positionsNeedingUpdate) {
    try {
      console.log(`Recalculating bond for position ${position.cusip} (ID: ${position.accountPositionMappingId})`);
      await recalculateAccountPosition(token, position.accountPositionMappingId);
    } catch (error) {
      console.error(`Error recalculating position ${position.accountPositionMappingId}:`, error);
      // Continue with other positions even if one fails
    }
  }
  
  console.log("Bond recalculation check completed.");
}
